import { BOW } from "@eldrun/content";
import { ballisticAt, lobPath as sharedLobPath, type Ballistic } from "@eldrun/sim";
import { Mesh, SphereGeometry, type Object3D } from "three";
import type { AtmosphereUniforms } from "../render/atmosphere.ts";
import { toonMaterial } from "../render/shaders/toon.ts";
import { unindexed } from "../render/geometry/unindexed.ts";

interface Lob {
  mesh: Mesh;
  path: Ballistic;
  depart: number;
  arrive: number;
  /** Quem arremessou (a criatura), para a cúpula do Bastião cortar o voo certo (F96). */
  owner: string | undefined;
}

/**
 * Curva do arremesso: a balística compartilhada (`libs/sim`) com a velocidade que leva de `from`
 * a `to` no tempo do voo, a mesma gravidade das flechas (e a mesma que o servidor testa na cúpula).
 */
export function lobPath(from: { x: number; y: number; z: number }, to: { x: number; y: number; z: number }, seconds: number): Ballistic {
  return sharedLobPath(from, to, seconds, BOW.gravity);
}

/**
 * Caldeirão da Cuca em voo (só visual): sai da mão no fim do preparo e cai no centro do aviso no
 * instante do impacto, pela curva balística compartilhada. O servidor resolve a queda no ponto
 * anunciado; o cliente só desenha o caminho.
 */
export class Lobs {
  private readonly root: Object3D;
  private readonly atmosphere: AtmosphereUniforms;
  private readonly geometry = unindexed(new SphereGeometry(0.28, 7, 5));
  private readonly lobs: Lob[] = [];

  constructor(root: Object3D, atmosphere: AtmosphereUniforms) {
    this.root = root;
    this.atmosphere = atmosphere;
  }

  spawn(from: { x: number; y: number; z: number }, to: { x: number; y: number; z: number }, depart: number, arrive: number, owner?: string): void {
    // O caldeirão arremessado é um objeto como os itens: na máscara do contorno (F51).
    const mesh = new Mesh(this.geometry, toonMaterial(this.atmosphere, "#2A2420", 0.6, false, { outline: true }));
    mesh.visible = false;
    this.root.add(mesh);
    this.lobs.push({ mesh, path: lobPath(from, to, Math.max(0.05, (arrive - depart) / 1000)), depart, arrive, owner });
  }

  /**
   * A cúpula do Bastião parou o arremesso de `owner` (F96): some o que chega primeiro dos que ainda
   * não caíram. Devolve se havia um.
   */
  block(owner: string, now: number): boolean {
    let found = -1;
    this.lobs.forEach((l, i) => {
      if (l.owner === owner && now < l.arrive && (found < 0 || l.arrive < (this.lobs[found] as Lob).arrive)) found = i;
    });
    if (found < 0) return false;
    this.root.remove((this.lobs[found] as Lob).mesh);
    this.lobs.splice(found, 1);
    return true;
  }

  update(now: number): void {
    for (let i = this.lobs.length - 1; i >= 0; i--) {
      const l = this.lobs[i] as Lob;
      if (now >= l.arrive) {
        this.root.remove(l.mesh);
        this.lobs.splice(i, 1);
        continue;
      }
      l.mesh.visible = now >= l.depart;
      if (!l.mesh.visible) continue;
      const p = ballisticAt(l.path, (now - l.depart) / 1000);
      l.mesh.position.set(p.x, p.y, p.z);
      l.mesh.rotation.x += 0.2;
    }
  }

  get count(): number {
    return this.lobs.length;
  }

  dispose(): void {
    for (const l of this.lobs) this.root.remove(l.mesh);
    this.lobs.length = 0;
    this.geometry.dispose();
  }
}
